import { Fragment, useCallback } from 'react';
import { CheckIcon as Check, XIcon as X } from './ui/Icons';
import { useAppStore } from '@/store/useAppStore';
import { recalculateStore } from '@/store/useAppStore';
import { AGSubject } from '@gpa-scales/shared-types';
import type { Course, CourseContribution } from '@gpa-scales/shared-types';
import { useToast } from '@/hooks/useToast';

const EMPTY_CONTRIBUTIONS: readonly CourseContribution[] = [];

const GRADE_LEVEL_LABELS: Record<number, string> = {
  9: '9th',
  10: '10th',
  11: '11th',
  12: '12th',
};

// Color encodes UC *weighting tier*, not "good vs. bad course." Adjacent types
// share a swatch because they share a rule in `engine/honorsEligibility.ts`:
//   - ap + ib_hl  → primary-light: both are always UC-honors-eligible (no
//     certification, no residency restriction).
//   - honors + ib_sl → info-light: both earn +1 only when `isUCCertifiedHonors`
//     is set (conditional tier).
//   - dual_enrollment + college → success-light: out-of-classroom/transfer tier.
// Splitting them into distinct steps of the navy ramp would imply a weighting
// distinction the engine does not actually make.
const COURSE_TYPE_COLORS: Record<string, string> = {
  standard: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]',
  honors: 'bg-[var(--color-info-light)] text-info',
  ap: 'bg-[var(--color-primary-light)] text-primary',
  ib_hl: 'bg-[var(--color-primary-light)] text-primary',
  ib_sl: 'bg-[var(--color-info-light)] text-info',
  dual_enrollment: 'bg-[var(--color-success-light)] text-success',
  college: 'bg-[var(--color-success-light)] text-success',
};

export function CourseTable() {
  const courses = useAppStore((s) => s.courses);
  const deleteCourse = useAppStore((s) => s.deleteCourse);
  const setEditingCourse = useAppStore((s) => s.setEditingCourse);
  const expandedCourseIds = useAppStore((s) => s.expandedCourseIds);
  const toggleCourseExpanded = useAppStore((s) => s.toggleCourseExpanded);
  const addCourse = useAppStore((s) => s.addCourse);
  const contributions = useAppStore((s) => s.result?.contributions ?? EMPTY_CONTRIBUTIONS);
  const { showToast } = useToast();

  // R27 — Undo-aware delete
  const handleDelete = useCallback(
    (course: Course) => {
      const { id: _id, ...rest } = course;
      deleteCourse(course.id);
      recalculateStore();
      showToast(
        `"${course.name}" deleted.`,
        'Undo',
        () => {
          addCourse(rest);
          recalculateStore();
        },
      );
    },
    [deleteCourse, addCourse, showToast],
  );

  if (courses.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-primary/40 bg-primary-light/40 p-8 text-center">
        <svg className="mx-auto mb-2 h-8 w-8 text-primary/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
        <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">No courses yet</h3>
        <p className="text-xs text-[var(--color-text-muted)]">
          Add your first course above, or use <span className="font-medium">&ldquo;Load Demo&rdquo;</span> in the header to explore a sample transcript.
        </p>
      </div>
    );
  }

  const getContribution = (courseId: string): CourseContribution | undefined =>
    contributions.find((c) => c.courseId === courseId);

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* R31 — Desktop table, mobile cards */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Course list with details and actions</caption>
          <thead className="bg-surface-secondary">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Course</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Level</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Term</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Grade</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Type</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">A-G</th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {courses.map((course) => {
              const contribution = getContribution(course.id);
              const isExpanded = expandedCourseIds.includes(course.id);
              return (
                <Fragment key={course.id}>
                  <tr className="hover:bg-surface-secondary transition-[var(--transition-fast)]">
                    <td className="px-3 py-2 font-medium text-[var(--color-text)]"><span className="block max-w-[200px] truncate" title={course.name}>{course.name}</span></td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)]">{GRADE_LEVEL_LABELS[course.gradeLevel]}</td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)] capitalize">{course.term}</td>
                    <td className="px-3 py-2 font-mono text-[var(--color-text)]">{course.grade}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${COURSE_TYPE_COLORS[course.courseType] ?? ''}`}>
                        {course.courseType.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                      {course.agSubject !== AGSubject.None ? course.agSubject : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => toggleCourseExpanded(course.id)}
                        className="mr-1 rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
                        aria-label="Toggle details"
                      >
                        <svg className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setEditingCourse(course.id)}
                        className="mr-1 rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
                        aria-label="Edit course"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                      <button
                        onClick={() => handleDelete(course)}
                        className="rounded p-1 text-[var(--color-text-muted)] hover:bg-error-light"
                        aria-label="Delete course"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </td>
                  </tr>
                  {isExpanded && contribution && (
                    <tr>
                      <td colSpan={7} className="bg-surface-secondary px-6 py-3 text-xs text-[var(--color-text-secondary)]">
                        <div className="grid gap-2 sm:grid-cols-3">
                          <div>
                            <span className="font-medium">Grade Points:</span> {contribution.gradePoints.toFixed(1)}
                          </div>
                          <div>
                            <span className="font-medium">Bonus:</span> +{contribution.bonusPoints.toFixed(1)}
                          </div>
                          <div>
                            <span className="font-medium">Credits:</span> {contribution.credits}
                          </div>
                        </div>
                        <div className="mt-2 flex gap-3 text-xs">
                          <span className={`flex items-center gap-1 ${contribution.includedIn.unweighted ? 'text-success' : 'text-error'}`}>
                            UW: {contribution.includedIn.unweighted ? <Check className="h-3 w-3" aria-hidden="true" /> : <X className="h-3 w-3" aria-hidden="true" />}
                          </span>
                          <span className={`flex items-center gap-1 ${contribution.includedIn.ucCapped ? 'text-success' : 'text-error'}`}>
                            UC Cap: {contribution.includedIn.ucCapped ? <Check className="h-3 w-3" aria-hidden="true" /> : <X className="h-3 w-3" aria-hidden="true" />}
                          </span>
                          <span className={`flex items-center gap-1 ${contribution.includedIn.ucUncapped ? 'text-success' : 'text-error'}`}>
                            UC Unc: {contribution.includedIn.ucUncapped ? <Check className="h-3 w-3" aria-hidden="true" /> : <X className="h-3 w-3" aria-hidden="true" />}
                          </span>
                          <span className={`flex items-center gap-1 ${contribution.includedIn.commonApp ? 'text-success' : 'text-error'}`}>
                            CA: {contribution.includedIn.commonApp ? <Check className="h-3 w-3" aria-hidden="true" /> : <X className="h-3 w-3" aria-hidden="true" />}
                          </span>
                        </div>
                        {contribution.explanation && (
                          <p className="mt-1 text-[var(--color-text-muted)]">{contribution.explanation}</p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* R31 — Mobile card layout */}
      <div className="sm:hidden divide-y divide-border">
        {courses.map((course) => (
          <div key={course.id} className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="allow-shrink min-w-0">
                <p className="font-medium text-sm text-[var(--color-text)] truncate" title={course.name}>{course.name}</p>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {GRADE_LEVEL_LABELS[course.gradeLevel]} · {course.term} · <span className="font-mono">{course.grade}</span>
                </p>
              </div>
              <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${COURSE_TYPE_COLORS[course.courseType] ?? ''}`}>
                {course.courseType.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--color-text-secondary)]">
                A-G: {course.agSubject !== AGSubject.None ? course.agSubject : '—'}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditingCourse(course.id)}
                  className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
                  aria-label="Edit course"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                </button>
                <button
                  onClick={() => handleDelete(course)}
                  className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-error-light"
                  aria-label="Delete course"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
